import React, { useRef, useState } from "react";
import { Upload, X, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { fileSizeLabel } from "../lib/constants";

/**
 * ClientUploadZone — drag-and-drop upload zone for clients to send files
 * back to A-Tech. Files default to `uploaded_by_role=client` so the practice
 * gets a "needs review" badge in the admin view.
 */
export default function ClientUploadZone({ projectId, folders = [], onUploaded }) {
  const [fileItems, setFileItems] = useState([]);
  const [targetFolder, setTargetFolder] = useState("");
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (list) => {
    const items = Array.from(list).map((f) => ({
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f,
      status: "pending",
      error: "",
    }));
    setFileItems((prev) => [...prev, ...items]);
    setDone(false);
  };

  const removeFile = (id) => setFileItems((prev) => prev.filter((item) => item.id !== id));

  const uploadOne = async (item) => {
    setFileItems((prev) => prev.map((f) => f.id === item.id ? { ...f, status: "uploading" } : f));
    const fd = new FormData();
    fd.append("file", item.file);
    fd.append("title", item.file.name.replace(/\.[^.]+$/, ""));
    fd.append("document_status", "issued_for_information");
    fd.append("visible_to_client", "true");
    fd.append("uploaded_by_role", "client");
    if (targetFolder) fd.append("folder_id", targetFolder);
    try {
      await api.post(`/projects/${projectId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFileItems((prev) => prev.map((f) => f.id === item.id ? { ...f, status: "done" } : f));
    } catch (e) {
      setFileItems((prev) => prev.map((f) =>
        f.id === item.id ? { ...f, status: "error", error: formatApiErrorDetail(e.response?.data?.detail) || e.message } : f
      ));
    }
  };

  const uploadAll = async () => {
    setUploading(true);
    const items = [...fileItems];
    const CONCURRENT = 3;
    for (let i = 0; i < items.length; i += CONCURRENT) {
      await Promise.all(items.slice(i, i + CONCURRENT).map((item) => uploadOne(item)));
    }
    setUploading(false);
    setDone(true);
    await onUploaded?.();
  };

  const total = fileItems.length;
  const doneCount = fileItems.filter((f) => f.status === "done").length;
  const cls = "w-full bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] px-3 py-2 text-sm text-[color:var(--atech-text)] focus:outline-none focus:border-[color:var(--atech-copper)]";

  return (
    <div className="mt-10" data-testid="client-upload-zone">
      <div className="label mb-3">Send files to A-Tech</div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-[2px] px-8 py-8 text-center cursor-pointer transition-all ${
          dragOver
            ? "border-[color:var(--atech-copper)] bg-[rgba(198,132,88,0.05)]"
            : "border-[color:var(--atech-border)] hover:border-[color:var(--atech-border-strong)] hover:bg-white/[0.02]"
        }`}
        data-testid="client-upload-drop-zone"
      >
        <Upload size={22} className="mx-auto mb-2 text-[color:var(--atech-text-muted)]" />
        <div className="text-sm text-[color:var(--atech-text)] font-medium">
          Drop files here or <span className="text-[color:var(--atech-copper)]">browse</span>
        </div>
        <div className="text-xs text-[color:var(--atech-text-muted)] mt-1">
          Site photos, surveys, correspondence — anything relevant to your project
        </div>
        <input ref={inputRef} type="file" multiple onChange={(e) => addFiles(e.target.files)} className="hidden" data-testid="client-upload-file-input" />
      </div>

      {total > 0 && (
        <>
          {folders.length > 0 && (
            <select
              value={targetFolder}
              onChange={(e) => setTargetFolder(e.target.value)}
              className={`${cls} mt-3`}
              data-testid="client-upload-folder-select"
            >
              <option value="">No folder (unfiled)</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {indentLabel(f, folders)}
                </option>
              ))}
            </select>
          )}
          <ul className="mt-3 space-y-1 max-h-48 overflow-y-auto">
            {fileItems.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-3 px-3 py-2 rounded-[2px] bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)]"
                data-testid={`client-upload-item-${item.id}`}
              >
                {item.status === "pending" && <div className="w-3.5 h-3.5 rounded-full border border-white/20 shrink-0" />}
                {item.status === "uploading" && <div className="w-3.5 h-3.5 rounded-full border-2 border-[color:var(--atech-copper)] border-t-transparent animate-spin shrink-0" />}
                {item.status === "done" && <CheckCircle2 size={14} className="text-[#34D399] shrink-0" />}
                {item.status === "error" && <span className="text-[#F87171] text-xs shrink-0">!</span>}
                <span className="flex-1 text-sm text-[color:var(--atech-text)] truncate">{item.file.name}</span>
                <span className="text-xs text-[color:var(--atech-text-muted)]">{fileSizeLabel(item.file.size)}</span>
                {!uploading && item.status === "pending" && (
                  <button onClick={(e) => { e.stopPropagation(); removeFile(item.id); }} className="text-[color:var(--atech-text-muted)] hover:text-[#F87171]">
                    <X size={13} />
                  </button>
                )}
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-end gap-3 mt-3">
            {done && (
              <span className="text-xs text-[#34D399] flex items-center gap-1">
                <CheckCircle2 size={12} /> {doneCount} sent
              </span>
            )}
            {!done && (
              <button
                onClick={() => { setFileItems([]); setDone(false); }}
                className="text-xs text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-text)]"
              >
                Clear
              </button>
            )}
            <button
              onClick={done ? () => { setFileItems([]); setDone(false); } : uploadAll}
              disabled={uploading || total === 0}
              className="btn-copper text-sm disabled:opacity-40"
              data-testid="client-upload-submit"
            >
              {done ? "Send more files" : uploading ? "Uploading…" : `Send ${total} file${total === 1 ? "" : "s"} to A-Tech`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Render folder name with depth-based indent for the dropdown. */
function indentLabel(folder, folders) {
  const byId = Object.fromEntries(folders.map((f) => [f.id, f]));
  let depth = 0;
  let p = folder.parent_id;
  while (p && byId[p] && depth < 10) {
    depth += 1;
    p = byId[p].parent_id;
  }
  return `${"— ".repeat(depth)}${folder.name}`;
}
